import { z } from "zod/v4";

import {
  CreateExperienceDtoDescriptions,
  ExperienceCurrencyDtoDescriptions,
  ExperienceDevicePreviewDtoDescriptions,
  ExperienceTestTypeDtoDescriptions,
  FullExperienceDtoDescriptions,
} from "../descriptions/experience-dto.describe";
import { assertDescriptions } from "../descriptions/types";
import { DeviceTypeList } from "../schemas/analytics/base";

import { AudienceDtoSchema } from "./audiences";
import { ExperienceActionDtoSchema } from "./experience-actions";
import {
  CreateExperienceAnalysisSchema,
  ExperienceAnalysisDtoSchema,
} from "./experience-analysis";
import { ExperienceCategoryList } from "./experience-categories";
import { ExperienceCustomMetricDtoSchema } from "./experience-custom-metrics";
import {
  CreateExperienceIntegrationDtoSchema,
  ExperienceIntegrationDtoSchema,
} from "./experience-integrations";
import { ExperienceKeyMetricDtoSchema } from "./experience-key-metrics";
import { ExperiencePageTargetingDtoSchema } from "./experience-page-targeting";
import { ExperienceProductTargetingDtoSchema } from "./experience-product-targeting";
import { OfferDtoSchema } from "./offers";
import { ProductVariantActionDtoSchema } from "./product-variants";
import { ShippingTestMethodDefinitionDtoSchema } from "./shipping-test-method-definitions";
import { RedactedVariationSchema, VariationDtoSchema } from "./variations";

export const ExperienceDevicePreviewDtoSchema = z
  .object({
    device: z
      .enum(DeviceTypeList)
      .default("desktop")
      .describe(ExperienceDevicePreviewDtoDescriptions.device),
    path: z
      .string()
      .default("/")
      .describe(ExperienceDevicePreviewDtoDescriptions.path),
  })
  .describe(ExperienceDevicePreviewDtoDescriptions._schema);

export type ExperienceDevicePreviewDto = z.infer<
  typeof ExperienceDevicePreviewDtoSchema
>;

export type HasExperienceTypeProperty =
  | "hasTestPricing"
  | "hasTestShipping"
  | "hasTestDiscount";

export const ExperienceStatusList = [
  "pending",
  "started",
  "ended",
  "paused",
] as const;

export type ExperienceStatus = (typeof ExperienceStatusList)[number];

export const ExternalExperienceTypeList = [
  "content/advanced",
  "content/onsiteEdits",
  "content/url",
  "content/theme",
  "content/template",
  "personalization",
  "personalization/offer",
  "pricing",
  "checkoutBlock",
] as const;

export type ExternalExperienceType =
  (typeof ExternalExperienceTypeList)[number];

export const ExperienceTypeList = [
  ...ExternalExperienceTypeList,
  "shipping",
  "sitewide",
  "postPurchase",
  "onsiteUpsell",
] as const;

export type ExperienceType = (typeof ExperienceTypeList)[number];

export const SourceActionTypeList = ["duplicate", "rollout"] as const;
export type SourceActionType = (typeof SourceActionTypeList)[number];

export const ShippingCarrierProviderList = ["intelligems", "custom"] as const;

export type ShippingCarrierProvider =
  (typeof ShippingCarrierProviderList)[number];

export const ExperienceCurrencyDtoSchema = z.object({
  code: z
    .string()
    .default("USD")
    .describe(ExperienceCurrencyDtoDescriptions.code),
  country: z
    .string()
    .optional()
    .describe(ExperienceCurrencyDtoDescriptions.country),
  options: z
    .any()
    .optional()
    .describe(ExperienceCurrencyDtoDescriptions.options),
  symbol: z
    .string()
    .default("$")
    .describe(ExperienceCurrencyDtoDescriptions.symbol),
  suffix: z
    .string()
    .optional()
    .describe(ExperienceCurrencyDtoDescriptions.suffix),
});

export type ExperienceCurrencyDto = z.infer<typeof ExperienceCurrencyDtoSchema>;

export const ExperienceTestTypeDtoSchema = z.object({
  hasTestPricing: z
    .boolean()
    .describe(ExperienceTestTypeDtoDescriptions.hasTestPricing),
  hasTestShipping: z
    .boolean()
    .describe(ExperienceTestTypeDtoDescriptions.hasTestShipping),
  hasTestCampaign: z
    .boolean()
    .describe(ExperienceTestTypeDtoDescriptions.hasTestCampaign),
  hasTestContent: z
    .boolean()
    .describe(ExperienceTestTypeDtoDescriptions.hasTestContent),
  hasTestContentUrl: z
    .boolean()
    .describe(ExperienceTestTypeDtoDescriptions.hasTestContentUrl),
  hasTestContentAdvanced: z
    .boolean()
    .describe(ExperienceTestTypeDtoDescriptions.hasTestContentAdvanced),
  hasTestContentOnsite: z
    .boolean()
    .describe(ExperienceTestTypeDtoDescriptions.hasTestContentOnsite),
  hasTestContentTemplate: z
    .boolean()
    .describe(ExperienceTestTypeDtoDescriptions.hasTestContentTemplate),
  hasTestContentTheme: z
    .boolean()
    .describe(ExperienceTestTypeDtoDescriptions.hasTestContentTheme),
  hasTestOnsiteInjections: z
    .boolean()
    .describe(ExperienceTestTypeDtoDescriptions.hasTestOnsiteInjections),
  hasTestCheckoutBlocks: z
    .boolean()
    .describe(ExperienceTestTypeDtoDescriptions.hasTestCheckoutBlocks),
  hasTestPostPurchase: z
    .boolean()
    .describe(ExperienceTestTypeDtoDescriptions.hasTestPostPurchase),
  hasTestOnsiteUpsell: z
    .boolean()
    .describe(ExperienceTestTypeDtoDescriptions.hasTestOnsiteUpsell),
});

export type ExperienceTestTypeDto = z.infer<typeof ExperienceTestTypeDtoSchema>;

export const FullExperienceDtoSchema = z.object({
  id: z.uuid().describe(FullExperienceDtoDescriptions.id),
  name: z.string().describe(FullExperienceDtoDescriptions.name),
  isPreview: z
    .boolean()
    .optional()
    .default(true)
    .describe(FullExperienceDtoDescriptions.isPreview),
  description: z
    .string()
    .nullish()
    .describe(FullExperienceDtoDescriptions.description),
  devicePreview: ExperienceDevicePreviewDtoSchema.nullish().describe(
    FullExperienceDtoDescriptions.devicePreview
  ),
  organizationId: z
    .uuid()
    .describe(FullExperienceDtoDescriptions.organizationId),
  type: z.enum(ExperienceTypeList).describe(FullExperienceDtoDescriptions.type),
  category: z
    .enum(ExperienceCategoryList)
    .describe(FullExperienceDtoDescriptions.category),
  status: z
    .enum(ExperienceStatusList)
    .describe(FullExperienceDtoDescriptions.status),
  testTypes: ExperienceTestTypeDtoSchema.describe(
    FullExperienceDtoDescriptions.testTypes
  ),
  experiencePageTargeting: ExperiencePageTargetingDtoSchema.array()
    .optional()
    .default([])
    .describe(FullExperienceDtoDescriptions.experiencePageTargeting),
  experienceProductTargeting: ExperienceProductTargetingDtoSchema.array()
    .optional()
    .default([])
    .describe(FullExperienceDtoDescriptions.experienceProductTargeting),
  experienceActions: ExperienceActionDtoSchema.array().describe(
    FullExperienceDtoDescriptions.experienceActions
  ),
  experienceAnalysis: ExperienceAnalysisDtoSchema.describe(
    FullExperienceDtoDescriptions.experienceAnalysis
  ),
  experienceIntegrations: ExperienceIntegrationDtoSchema.array()
    .default([])
    .describe(FullExperienceDtoDescriptions.experienceIntegrations),
  experienceCustomMetrics: ExperienceCustomMetricDtoSchema.array()
    .default([])
    .describe(FullExperienceDtoDescriptions.experienceCustomMetrics),
  experienceKeyMetrics: ExperienceKeyMetricDtoSchema.array()
    .default([])
    .describe(FullExperienceDtoDescriptions.experienceKeyMetrics),
  currency: ExperienceCurrencyDtoSchema.nullish().describe(
    FullExperienceDtoDescriptions.currency
  ),
  variations: VariationDtoSchema.array()
    .default([])
    .describe(FullExperienceDtoDescriptions.variations),
  createdAtTs: z.number().describe(FullExperienceDtoDescriptions.createdAtTs),
  isDeleted: z
    .boolean()
    .optional()
    .describe(FullExperienceDtoDescriptions.isDeleted),
  shippingTestMethodDefinitions: ShippingTestMethodDefinitionDtoSchema.array()
    .default([])
    .describe(FullExperienceDtoDescriptions.shippingTestMethodDefinitions),
  shippingCarrierProvider: z
    .enum(ShippingCarrierProviderList)
    .default("intelligems")
    .describe(FullExperienceDtoDescriptions.shippingCarrierProvider),
  audience: AudienceDtoSchema.nullish().describe(
    FullExperienceDtoDescriptions.audience
  ),
  // userInterfaces: UserInterfaceDtoSchema.array()
  //   .default([])
  //   .describe("UI widgets and interfaces displayed for this experience"),
  lastUpdateTs: z.number().describe(FullExperienceDtoDescriptions.lastUpdateTs),
  previewPath: z
    .string()
    .nullish()
    .describe(FullExperienceDtoDescriptions.previewPath),
  isExternal: z
    .boolean()
    .default(false)
    .nullish()
    .describe(FullExperienceDtoDescriptions.isExternal),
  startedAtTs: z
    .number()
    .nullable()
    .describe(FullExperienceDtoDescriptions.startedAtTs),
  pausedAtTs: z
    .number()
    .nullable()
    .describe(FullExperienceDtoDescriptions.pausedAtTs),
  archivedAtTs: z
    .number()
    .optional()
    .nullish()
    .describe(FullExperienceDtoDescriptions.archivedAtTs),
  requiresLink: z
    .boolean()
    .optional()
    .default(false)
    .describe(FullExperienceDtoDescriptions.requiresLink),
  linkBaseUrl: z
    .string()
    .nullable()
    .describe(FullExperienceDtoDescriptions.linkBaseUrl),
  endedAtTs: z
    .number()
    .nullable()
    .describe(FullExperienceDtoDescriptions.endedAtTs),
  productVariantCount: z
    .number()
    .default(0)
    .describe(FullExperienceDtoDescriptions.productVariantCount),
  sourceExperienceId: z
    .uuid()
    .optional()
    .nullable()
    .describe(FullExperienceDtoDescriptions.sourceExperienceId),
  sourceAction: z
    .enum(SourceActionTypeList)
    .optional()
    .nullable()
    .describe(FullExperienceDtoDescriptions.sourceAction),
  shareToken: z
    .string()
    .nullish()
    .describe(FullExperienceDtoDescriptions.shareToken),
});

export const ExperienceDtoSchema = FullExperienceDtoSchema.omit({
  shareToken: true,
});

export type ExperienceDto = z.infer<typeof ExperienceDtoSchema>;

export const InternalExperienceDtoSchema = FullExperienceDtoSchema;

export type InternalExperienceDto = z.infer<typeof InternalExperienceDtoSchema>;

export type ExperienceSnapshot = {
  experience: InternalExperienceDto;
};

export const CreateExperienceDtoSchema = ExperienceDtoSchema.omit({
  experienceActions: true,
  experiencePageTargeting: true,
  experienceProductTargeting: true,
  variations: true,
  audience: true,
  createdAtTs: true,
  organizationId: true,
  lastUpdateTs: true,
  experienceKeyMetrics: true,
  experienceCustomMetrics: true,
}).extend({
  id: z.uuid().optional().describe(CreateExperienceDtoDescriptions.id),
  productVariantAction: ProductVariantActionDtoSchema.optional(),
  experienceIntegrations: CreateExperienceIntegrationDtoSchema.array()
    .default([])
    .describe(CreateExperienceDtoDescriptions.experienceIntegrations),
  experienceAnalysis: CreateExperienceAnalysisSchema.describe(
    CreateExperienceDtoDescriptions.experienceAnalysis
  ),
  name: z.string().min(1).describe(CreateExperienceDtoDescriptions.name),
  description: FullExperienceDtoSchema.shape.description.describe(
    CreateExperienceDtoDescriptions.description
  ),
  currency: FullExperienceDtoSchema.shape.currency.describe(
    CreateExperienceDtoDescriptions.currency
  ),
  previewPath: FullExperienceDtoSchema.shape.previewPath.describe(
    CreateExperienceDtoDescriptions.previewPath
  ),
  type: FullExperienceDtoSchema.shape.type.describe(
    CreateExperienceDtoDescriptions.type
  ),
  isPreview: FullExperienceDtoSchema.shape.isPreview.describe(
    CreateExperienceDtoDescriptions.isPreview
  ),
});

export type CreateExperienceDto = z.infer<typeof CreateExperienceDtoSchema>;

export const PatchExperienceDtoSchema = z.object({
  id: z.uuid(),
  name: z.string().optional(),
  isPreview: z.boolean().optional(),
  description: z.string().optional(),
  isDeleted: z.boolean().optional(),
  archivedAtTs: z.number().optional().nullish(),
  devicePreview: ExperienceDevicePreviewDtoSchema.optional(),
});

export type PatchExperienceDto = z.infer<typeof PatchExperienceDtoSchema>;

export const ExperienceVariationDtoScheme = z.object({
  experience: ExperienceDtoSchema,
  variation: VariationDtoSchema,
  offer: OfferDtoSchema,
});

export type ExperienceVariationDto = z.infer<
  typeof ExperienceVariationDtoScheme
>;

export const RedactedExperienceSchema = ExperienceDtoSchema.omit({
  experiencePageTargeting: true,
  experienceProductTargeting: true,
  experienceAnalysis: true,
  experienceIntegrations: true,
  experienceCustomMetrics: true,
  experienceKeyMetrics: true,
  variations: true,
  shippingTestMethodDefinitions: true,
  audience: true,
}).extend({
  variations: RedactedVariationSchema.array().default([]),
});

export type RedactedExperienceDto = z.infer<typeof RedactedExperienceSchema>;

// Compile-time checks: description objects must cover all schema keys
assertDescriptions<
  typeof ExperienceDevicePreviewDtoDescriptions,
  ExperienceDevicePreviewDto
>();
assertDescriptions<
  typeof ExperienceCurrencyDtoDescriptions,
  ExperienceCurrencyDto
>();
assertDescriptions<
  typeof ExperienceTestTypeDtoDescriptions,
  ExperienceTestTypeDto
>();
assertDescriptions<
  typeof FullExperienceDtoDescriptions,
  InternalExperienceDto
>();
